import {
  Accordion,
  AccordionSummary,
  Box,
  Button,
  SxProps,
  Typography,
  Zoom,
  useTheme,
} from '@mui/material';
import { FC, RefObject, createRef, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAuth } from '@/logic/state';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { PROFILE_CARDS_TRANSITION_TIMEOUT } from '@/constants/profile';
import { WalletAccordionDetails } from './wallet-accordion-details';

import { WalletInfo } from './wallet-info';
import { WalletNotCompletedProfileInfo } from './wallet-not-completed-profile-info';
import { WalletNotVerifiedInfo } from './wallet-not-verified-info';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useWalletBalance } from '../../hooks/use-wallet-balance';
import { useGetTransactions } from '../../services/_api';
import { useRouter } from 'next/router';
import { useScrollToItem } from '@/logic/hooks';
import { useUserBalance } from '../../hooks/use-user-balance';
import ImageBoxFuture from '@/components/shared/image-box-future';
import { MovieLoaderWrapper } from '@/components/shared/movie-loader-wrapper';
import { E_WALLET } from '../../services/types';

const WalletIcon = '/icons/profile/transactions/wallet.svg';
const NotfoundIcon = '/icons/shared/not-found-no-data.svg';
interface MyWalletCollapseProps {
  accordionSx: SxProps;
  accordionBoxIconSx: SxProps;
  accordionTitleSx: SxProps;
}
const MyWalletCollapse: FC<MyWalletCollapseProps> = ({
  accordionSx,
  accordionBoxIconSx,
  accordionTitleSx,
}) => {
  const theme = useTheme();
  const { asPath } = useRouter();

  const [myWalletRef, setRef] = useState<RefObject<HTMLDivElement>>();
  const intl = useIntl();
  const user = useAuth(s => s.user, shallow);

  const hash = asPath.split('#')[1];
  const isMyWallyHashExist = hash === 'my-wallet';
  const [isMyWalletExpanded, setMyWalletExpanded] = useState(false);

  const { scrollToItem } = useScrollToItem();

  const isUserAuth = Boolean(user?.isCompleteProfile && user?.isVerified);

  const { isFetchingBalance, isErrorBalance, isSuccessBalance, refetchBalance } =
    useWalletBalance();
  const { userFormattedWalletBalance } = useUserBalance();

  const {
    data: transactionsData,
    isFetching: isFetchingTransactions,
    isError: isErrorTransactions,
    refetch: refetchTransactions,
  } = useGetTransactions(
    {
      params: {
        limit: 10,
        page: 1,
        walletType: E_WALLET,
      },
    },
    {
      enabled: isUserAuth,
    }
  );

  const handleChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    setMyWalletExpanded(isExpanded);
  };

  useEffect(() => {
    const ref = createRef<HTMLDivElement>();
    setRef(ref);
  }, []);

  useEffect(() => {
    if (isMyWalletExpanded && myWalletRef && isMyWallyHashExist)
      myWalletRef.current && scrollToItem(myWalletRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myWalletRef?.current, asPath]);

  useEffect(() => {
    if (isMyWallyHashExist) {
      setMyWalletExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath]);

  return (
    <Zoom in timeout={PROFILE_CARDS_TRANSITION_TIMEOUT}>
      <Accordion
        sx={{ maxWidth: '910px' }}
        expanded={isMyWalletExpanded}
        onChange={handleChange}
      >
        <AccordionSummary
          id={'open-wallet'}
          expandIcon={
            <ExpandMoreIcon
              sx={{
                color: theme.palette.supporting.navy,
                width: { tablet: pxToRem(24), mobile: '24px' },
                height: { tablet: pxToRem(24), mobile: '24px' },
                alignItems: 'center',
              }}
            />
          }
          sx={{
            ...accordionSx,
            justifyContent: 'space-between',
          }}
        >
          <Box
            component='div'
            sx={{
              ...accordionBoxIconSx,
              backgroundColor: theme.palette.supporting.navy,
            }}
          >
            <ImageBoxFuture
              img={WalletIcon}
              title={intl.formatMessage({
                id: 'alt.my_wallet',
              })}
              objectFit='contain'
              sx={{
                width: { tablet: pxToRem(33), mobile: '40px' },
                height: { tablet: pxToRem(33), mobile: '24px' },
              }}
            />
          </Box>
          <Typography component='h2' sx={accordionTitleSx}>
            <FormattedMessage id='wallet.my.wallet' />
          </Typography>
          <Typography
            id={'wallet-amount'}
            component='h2'
            sx={{
              ...accordionTitleSx,
              flexGrow: 1,
              textAlign: 'end',
              marginInlineEnd: '12px',
            }}
          >
            {isUserAuth ? (
              <>
                {userFormattedWalletBalance}&nbsp;
                <FormattedMessage id='currency.SAR' />
              </>
            ) : (
              <FormattedMessage id='wallet.know_more' />
            )}
          </Typography>
        </AccordionSummary>
        {isUserAuth ? (
          isFetchingBalance ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <MovieLoaderWrapper outerBox={{ width: '200px' }} />
            </Box>
          ) : isErrorBalance ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                marginBlock: '64px',
              }}
            >
              <ImageBoxFuture
                img={NotfoundIcon}
                sx={{ width: pxToRem(288), height: pxToRem(178) }}
              />
              <Typography
                sx={{
                  fontSize: pxToRem(20),
                  fontWeight: '400',
                  color: theme.palette.grey[50],
                  marginTop: '34px',
                }}
              >
                <FormattedMessage id='movie_finder.sorry.something.went.wrong' />
              </Typography>
              <Button
                loading={isFetchingBalance}
                variant='contained'
                sx={{ marginTop: '32px', width: '178px', height: '60px' }}
                onClick={() => refetchBalance()}
              >
                <FormattedMessage id='global.try_again' />
              </Button>
            </Box>
          ) : isSuccessBalance ? (
            <WalletAccordionDetails>
              <WalletInfo
                myWalletRef={myWalletRef}
                accordionSx={accordionSx}
                accordionBoxIconSx={accordionBoxIconSx}
                transactions={transactionsData?.data ?? []}
                isFetchingTransactions={isFetchingTransactions}
                isErrorTransactions={isErrorTransactions}
                refetchTransactions={refetchTransactions}
              />
            </WalletAccordionDetails>
          ) : null
        ) : user?.isCompleteProfile && !user?.isVerified ? (
          <WalletAccordionDetails>
            <WalletNotVerifiedInfo myWalletRef={myWalletRef} accordionSx={accordionSx} />
          </WalletAccordionDetails>
        ) : !user?.isCompleteProfile && !user?.isVerified ? (
          <WalletAccordionDetails>
            <WalletNotCompletedProfileInfo
              myWalletRef={myWalletRef}
              accordionSx={accordionSx}
            />
          </WalletAccordionDetails>
        ) : null}
      </Accordion>
    </Zoom>
  );
};

export default MyWalletCollapse;
